import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { normalizeAudienceMode, type AudienceMode } from "../lib/audienceMode";
import { loadPersistedUi } from "../lib/persistedUi";

type AudienceModeContextValue = {
  audienceMode: AudienceMode;
  setAudienceMode: (m: AudienceMode) => void;
};

const AudienceModeContext = createContext<AudienceModeContextValue | null>(null);

export function AudienceModeProvider({ children }: { children: ReactNode }) {
  const [audienceMode, setAudienceModeState] = useState<AudienceMode>(() =>
    normalizeAudienceMode(loadPersistedUi()?.audienceMode),
  );

  const setAudienceMode = useCallback((m: AudienceMode) => {
    setAudienceModeState(m);
  }, []);

  const value = useMemo(
    () => ({ audienceMode, setAudienceMode }),
    [audienceMode, setAudienceMode],
  );

  return (
    <AudienceModeContext.Provider value={value}>
      {children}
    </AudienceModeContext.Provider>
  );
}

export function useAudienceMode(): AudienceModeContextValue {
  const ctx = useContext(AudienceModeContext);
  if (!ctx) throw new Error("useAudienceMode must be used within AudienceModeProvider");
  return ctx;
}
